/**
 * CSS Variables Helper
 * Provides type-safe access to CSS custom properties with autocomplete
 * Import and use: import { cssVar } from '@styles/cssVars';
 * Usage: style={{ color: cssVar.colorMainAction }}
 */

export const designSystemStyles = {
    // Fonts
    fontFamily: 'var(--font-family)' as const,
    fontFamilyMonospace: 'var(--font-family-monospace)' as const,

    // Font Sizes
    fontSizeXxsm: 'var(--font-size-xxsm)' as const,
    fontSizeXsm: 'var(--font-size-xsm)' as const,
    fontSizeSm: 'var(--font-size-sm)' as const,
    fontSizeMdSmall: 'var(--font-size-mdSmall)' as const,
    fontSizeMd: 'var(--font-size-md)' as const,
    fontSizeLg: 'var(--font-size-lg)' as const,
    fontSizeTitle: 'var(--font-size-title)' as const,
    fontSizeWarning: 'var(--font-size-warning)' as const,
    fontSizeMainAction: 'var(--font-size-main-action)' as const,
    fontSizeCancelAction: 'var(--font-size-cancel-action)' as const,
    fontSizeDropdownText: 'var(--font-size-dropdown-text)' as const,
    lineHeightText: 'var(--line-height-text)' as const,
    letterSpacingSectionLabel: 'var(--letter-spacing-section-label)' as const,

    // Font Weights
    fontWeightText: 'var(--font-weight-text)' as const,
    fontWeightTitle: 'var(--font-weight-title)' as const,
    fontWeightWarning: 'var(--font-weight-warning)' as const,
    fontWeightMainAction: 'var(--font-weight-main-action)' as const,
    fontWeightCancelAction: 'var(--font-weight-cancel-action)' as const,
    fontWeightDropdownText: 'var(--font-weight-dropdown-text)' as const,

    // Font Colors
    fontColorPrimary: 'var(--font-color-primary)' as const,
    fontColorSecondary: 'var(--font-color-secondary)' as const,
    fontColorTertiary: 'var(--font-color-tertiary)' as const,

    // Line Heights
    lineHeightWarning: 'var(--line-height-warning)' as const,
    lineHeightNormal: 'var(--line-height-normal)' as const,

    paddingPillSmall: 'var(--padding-pill-small)' as const,

    // Skeleton shimmer (loading placeholders)
    colorSkeletonBase: 'var(--color-skeleton-base)' as const,
    colorSkeletonHighlight: 'var(--color-skeleton-highlight)' as const,

    // Border Radius
    cornerRadius: 'var(--corner-radius)' as const,
    cornerRadiusComponent: 'var(--corner-radius-component)' as const,
    radiusButton: 'var(--radius-button)' as const,
    radiusDropdown: 'var(--radius-dropdown)' as const,
    radiusItem: 'var(--radius-item)' as const,
    radiusModal: 'var(--radius-modal)' as const,
    radiusCard: 'var(--radius-card)' as const,
    radiusPill: 'var(--radius-pill)' as const,
    radiusToast: 'var(--radius-toast)' as const,

    // Sleek tab group (TabScreenSleek)
    radiusTabGroup: 'var(--radius-tab-group)' as const,
    radiusTabItem: 'var(--radius-tab-item)' as const,
    paddingTabGroup: 'var(--padding-tab-group)' as const,
    paddingTabItem: 'var(--padding-tab-item)' as const,
    gapTabGroup: 'var(--gap-tab-group)' as const,
    colorTabGroupBackground: 'var(--color-tab-group-background)' as const,
    colorTabTextInactive: 'var(--color-tab-text-inactive)' as const,
    colorTabTextActive: 'var(--color-tab-text-active)' as const,
    fontWeightTabInactive: 'var(--font-weight-tab-inactive)' as const,
    fontWeightTabActive: 'var(--font-weight-tab-active)' as const,
    lineHeightTab: 'var(--line-height-tab)' as const,
    shadowTabActive: 'var(--shadow-tab-active)' as const,
    transitionTab: 'var(--transition-tab)' as const,

    // Spacing
    spaceXtraSm: 'var(--space-xtra-sm)' as const,
    spaceSm: 'var(--space-sm)' as const,
    spaceMd: 'var(--space-md)' as const,
    spaceLg: 'var(--space-lg)' as const,

    // Margins
    marginSm: 'var(--margin-sm)' as const,
    marginMd: 'var(--margin-md)' as const,
    marginLg: 'var(--margin-lg)' as const,
    marginDropdownBottom: 'var(--margin-dropdown-bottom)' as const,
    marginItemBottom: 'var(--margin-item-bottom)' as const,

    // Padding
    paddingZero: '0' as const,
    paddingXxsm: 'var(--padding-xxsm)' as const,
    paddingXsm: 'var(--padding-xsm)' as const,
    paddingSm: 'var(--padding-sm)' as const,
    paddingMd: 'var(--padding-md)' as const,
    paddingLg: 'var(--padding-lg)' as const,
    paddingLgScreen: 'var(--padding-lg-screen)' as const,
    paddingHzComponent: 'var(--padding-hz-component)' as const,
    paddingPopup: 'var(--padding-popup)' as const,
    paddingPopupWithText: 'var(--padding-popup-with-text)' as const,
    paddingButton: 'var(--padding-button)' as const,
    paddingDropdownHorizontal: 'var(--padding-dropdown-horizontal)' as const,
    paddingDropdownExpandedVertical: 'var(--padding-dropdown-expanded-vertical)' as const,
    paddingItemLeft: 'var(--padding-item-left)' as const,
    paddingPill: 'var(--padding-pill)' as const,

    // Gaps
    gapNone: "0px" as const,
    gapXxxSm: 'var(--gap-xxxSm)' as const,
    gapXsm: 'var(--gap-xsm)' as const,
    gapSm: 'var(--gap-sm)' as const,
    gapMd: 'var(--gap-md)' as const,
    gapLg: 'var(--gap-lg)' as const,
    gapBottomContainer: 'var(--gap-bottom-container)' as const,

    // Sizes
    sizeLargePopupWidth: 'var(--size-large-popup-width)' as const,
    sizeLargePopupHeight: 'var(--size-large-popup-height)' as const,
    sizeButtonWidth: 'var(--size-button-width)' as const,
    sizeButtonHeight: 'var(--size-button-height)' as const,
    sizeTabHeight: 'var(--size-tab-height)' as const,
    sizeLoading: 'var(--size-loading)' as const,
    sizeDropdownHeight: 'var(--size-dropdown-height)' as const,
    sizeDropdownExpandedHeight: 'var(--size-dropdown-expanded-height)' as const,
    sizeItemHeight: 'var(--size-item-height)' as const,
    sizeLeftMenuHeaderHeight: 'var(--size-left-menu-header-height)' as const,
    gapLeftMenuItems: 'var(--gap-left-menu-items)' as const,
    marginLeftMenuSection: 'var(--margin-left-menu-section)' as const,
    sizeRowMedium: 'var(--size-row-medium)' as const,
    sizeIconWidth: 'var(--size-icon-width)' as const,
    sizeIconHeight: 'var(--size-icon-height)' as const,

    sizeIconLg: 'var(--size-icon-lg)' as const,
    sizeIconMd: 'var(--size-icon-md)' as const,
    sizeIconSm: 'var(--size-icon-sm)' as const,

    sizeAvatar: 'var(--size-avatar)' as const,
    sizeBottomSheetSm: 'var(--size-bottom-sheet-sm)' as const,
    sizeBottomSheetMd: 'var(--size-bottom-sheet-md)' as const,
    sizeBottomSheetLg: 'var(--size-bottom-sheet-lg)' as const,
    sizeOverflowMenuMinWidth: 'var(--size-overflow-menu-min-width)' as const,
    sizeDot: 'var(--size-dot)' as const,
    sizeCodeBoxMobile: 'var(--size-code-box-mobile)' as const,
    sizeCodeBoxDesktop: 'var(--size-code-box-desktop)' as const,
    sizeCodeDot: 'var(--size-code-dot)' as const,
    sizeCodeCaretWidth: 'var(--size-code-caret-width)' as const,
    gapCodeBox: 'var(--gap-code-box)' as const,
    durationCodeCaretBlink: 'var(--duration-code-caret-blink)' as const,
    sizeButtonWidthWide: 'var(--size-button-width-wide)' as const,
    sizeProgressBar: 'var(--size-progress-bar)' as const,
    durationProgressBar: 'var(--duration-progress-bar)' as const,
    sizeIconBadge: 'var(--size-icon-badge)' as const,
    sizeToastMaxWidth: 'var(--size-toast-max-width)' as const,

    // Compact icon menu (theme selector & co.)
    sizeIconButton: 'var(--size-icon-button)' as const,
    sizeMenuWidth: 'var(--size-menu-width)' as const,
    paddingMenu: 'var(--padding-menu)' as const,
    paddingMenuItem: 'var(--padding-menu-item)' as const,
    paddingMenuSectionLabel: 'var(--padding-menu-section-label)' as const,
    gapMenuItems: 'var(--gap-menu-items)' as const,
    gapMenuItemContent: 'var(--gap-menu-item-content)' as const,
    marginMenuOffset: 'var(--margin-menu-offset)' as const,
    fontSizeMenuItem: 'var(--font-size-menu-item)' as const,
    fontWeightMenuItem: 'var(--font-weight-menu-item)' as const,
    transitionMenuItem: 'var(--transition-menu-item)' as const,

    // Palette Colors
    colorBackgroundWhite: 'var(--color-background-white)' as const,
    colorQrSurface: 'var(--color-qr-surface)' as const,
    colorGreen: 'var(--color-green)' as const,
    colorRed: 'var(--color-red)' as const,
    colorBackgroundGray2: 'var(--color-background-gray2)' as const,
    colorBackgroundGray3: 'var(--color-background-gray3)' as const,
    colorSurfaceSubtle: 'var(--color-surface-subtle)' as const,
    colorSurfaceMuted: 'var(--color-surface-muted)' as const,
    colorBorderSubtle: 'var(--color-border-subtle)' as const,
    colorBorderMuted: 'var(--color-border-muted)' as const,
    colorDividerSubtle: 'var(--color-divider-subtle)' as const,
    colorDividerMuted: 'var(--color-divider-muted)' as const,
    dividerThickness: 'var(--divider-thickness)' as const,
    colorInfo: 'var(--color-info)' as const,

    // Component Colors
    colorPopupBackground: 'var(--color-popup-background)' as const,
    colorMainAction: 'var(--color-main-action)' as const,
    colorMainActionWashed: 'var(--color-main-action-washed)' as const,
    colorTextInputBorder: 'var(--color-text-input-border)' as const,
    colorError: 'var(--color-error)' as const,
    colorCancelAction: 'var(--color-cancel-action)' as const,
    colorTintedAction: 'var(--color-main-tinted-action)' as const,
    colorSwitchTrackOn: 'var(--color-switch-track-on)' as const,
    colorSwitchTrackOff: 'var(--color-switch-track-off)' as const,
    colorSwitchThumb: 'var(--color-switch-thumb)' as const,
    colorDropdownBackground: 'var(--color-dropdown-background)' as const,
    colorDropdownHover: 'var(--color-dropdown-hover)' as const,
    colorDropdownText: 'var(--color-dropdown-text)' as const,
    colorItemBackground: 'var(--color-item-background)' as const,
    colorItemClickedBackground: 'var(--color-item-clicked-background)' as const,
    colorItemHoverBackground: 'var(--color-item-hover-background)' as const,
    colorDisabledBackground: 'var(--color-disabled-background)' as const,
    colorPresenteeismGreen: 'var(--color-presenteeism-green)' as const,
    colorPresenteeismRed: 'var(--color-presenteeism-red)' as const,
    colorPresenteeismYellow: 'var(--color-presenteeism-yellow)' as const,
    colorPresenteeismDefault: 'var(--color-presenteeism-default)' as const,
    colorUserInitialsBorder: 'var(--color-user-initials-border)' as const,
    colorUserInitialsBackground: 'var(--color-user-initials-background)' as const,
    colorIconButtonBackground: 'var(--color-background-gray4)' as const,
    colorPlaceholderBackground: 'var(--color-background-gray3)' as const,
    colorTooltipBackground: 'var(--color-tooltip-background)' as const,
    colorTooltipText: 'var(--color-tooltip-text)' as const,

    // Text Colors
    colorWarningText: 'var(--color-warning-text)' as const,
    colorTitleText: 'var(--color-title-text)' as const,
    colorText: 'var(--color-text)' as const,
    colorTextStrong: 'var(--color-text-strong)' as const,
    colorTextBody: 'var(--color-text-body)' as const,
    colorTextSupporting: 'var(--color-text-supporting)' as const,
    colorTextMuted: 'var(--color-text-muted)' as const,
    colorTextSecondary: 'var(--color-text-secondary)' as const,

    // Meaning Colors
    colorSuccess: 'var(--color-success)' as const,
    colorRejected: 'var(--color-rejected)' as const,
    colorWarning: 'var(--color-warning)' as const,
    colorInProgress: 'var(--color-in-progress)' as const,

    // Status Pill Colors (dot colors; green/red reserved for outcomes)
    colorStatusPillBackground: 'var(--color-status-pill-background)' as const,
    colorStatusNeutral: 'var(--color-status-neutral)' as const,
    colorStatusInfo: 'var(--color-status-info)' as const,
    colorStatusActive: 'var(--color-status-active)' as const,
    colorStatusSuccess: 'var(--color-status-success)' as const,
    colorStatusError: 'var(--color-status-error)' as const,
    colorStatusVoided: 'var(--color-status-voided)' as const,

    // Toast Banner Colors
    colorToastInfoBackground: 'var(--color-toast-info-background)' as const,
    colorToastSuccessBackground: 'var(--color-toast-success-background)' as const,
    colorToastErrorBackground: 'var(--color-toast-error-background)' as const,
    colorToastWarningBackground: 'var(--color-toast-warning-background)' as const,
    colorToastText: 'var(--color-toast-text)' as const,

    // Transitions
    transitionDuration: 'var(--transition-duration)' as const,
    transitionEase: 'var(--transition-ease)' as const,
    opacityTransitionEase: 'opacity var(--transition-duration) var(--transition-ease)' as const,
    transitionIcon: 'var(--transition-icon)' as const,

    // Font Combinations (from variables.css)
    interRegular: 'var(--inter-regular)' as const,
    interBold: 'var(--inter-bold)' as const,
    interTitle: 'var(--inter-title)' as const,

    borderDisabledButton: '1px solid var(--color-disabled-background)' as const,

    justifyContentRight: 'flex-end' as const,
    justifyContentLeft: 'flex-start' as const,

    shadowCard: '0px 2px 6px 0px rgba(0, 0, 0, 0.10)' as const,
    shadowTextInput: '0 2px 2px 0 rgba(33, 37, 41, 0.06), 0 0 1px 0 rgba(33, 37, 41, 0.08)' as const,
    shadowModal: 'var(--shadow-modal)' as const,
    shadowToast: 'var(--shadow-toast)' as const,

    zIndexDropdown: 'var(--z-index-dropdown)' as const,
    zIndexToast: 'var(--z-index-toast)' as const,

    // Scrollbars
    scrollbarSize: 'var(--scrollbar-size)' as const,
    scrollbarTrack: 'var(--scrollbar-track)' as const,
    scrollbarThumb: 'var(--scrollbar-thumb)' as const,
    scrollbarThumbHover: 'var(--scrollbar-thumb-hover)' as const,
    scrollbarRadius: 'var(--scrollbar-radius)' as const,

    // Platform notices
    noticeCriticalBg: 'var(--notice-critical-bg)' as const,
    noticeCriticalFg: 'var(--notice-critical-fg)' as const,
    noticeCriticalBorder: 'var(--notice-critical-border)' as const,
    noticeCriticalIcon: 'var(--notice-critical-icon)' as const,
    noticeWarningBg: 'var(--notice-warning-bg)' as const,
    noticeWarningFg: 'var(--notice-warning-fg)' as const,
    noticeWarningBorder: 'var(--notice-warning-border)' as const,
    noticeWarningIcon: 'var(--notice-warning-icon)' as const,
    noticeCommunicationBg: 'var(--notice-communication-bg)' as const,
    noticeCommunicationFg: 'var(--notice-communication-fg)' as const,
    noticeCommunicationBorder: 'var(--notice-communication-border)' as const,
    noticeCommunicationIcon: 'var(--notice-communication-icon)' as const,
    noticeRowMinHeight: 'var(--notice-row-min-height)' as const,
    noticePadding: 'var(--notice-padding)' as const,
    noticePaddingNarrow: 'var(--notice-padding-narrow)' as const,
    noticeCardPadding: 'var(--notice-card-padding)' as const,
    noticeGap: 'var(--notice-gap)' as const,
    noticeRadius: 'var(--notice-radius)' as const,
    noticeFontSize: 'var(--notice-font-size)' as const,
    noticeLineHeight: 'var(--notice-line-height)' as const,
    noticeTitleWeight: 'var(--notice-title-weight)' as const,
    noticeMetaSize: 'var(--notice-meta-size)' as const,
    noticePillFontSize: 'var(--notice-pill-font-size)' as const,
    noticePillPadding: 'var(--notice-pill-padding)' as const,
    noticeControlPadding: 'var(--notice-control-padding)' as const,
    noticeControlRadius: 'var(--notice-control-radius)' as const,
    noticeControlHover: 'var(--notice-control-hover)' as const,
    noticeDismissSize: 'var(--notice-dismiss-size)' as const,
    noticeIconSize: 'var(--notice-icon-size)' as const,
    noticeBodyMaxLines: 'var(--notice-body-max-lines)' as const,
    noticePanelWidth: 'var(--notice-panel-width)' as const,
    noticeAckModalWidth: 'var(--notice-ack-modal-width)' as const,
    noticeModalFontSize: 'var(--notice-modal-font-size)' as const,
    noticeModalLineHeight: 'var(--notice-modal-line-height)' as const,
    noticePanelTitleSize: 'var(--notice-panel-title-size)' as const,
    noticePillBg: 'var(--notice-pill-bg)' as const,
    noticePanelMaxHeight: 'var(--notice-panel-max-height)' as const,
    noticePanelOffset: 'var(--notice-panel-offset)' as const,
    noticePanelBg: 'var(--notice-panel-bg)' as const,
    noticePanelRadius: 'var(--notice-panel-radius)' as const,
    noticePanelShadow: 'var(--notice-panel-shadow)' as const,
    noticeFocusRing: 'var(--notice-focus-ring)' as const,
    noticeFocusOffset: 'var(--notice-focus-offset)' as const,
    noticeLinkUnderlineOffset: 'var(--notice-link-underline-offset)' as const,
    noticeMotionIn: 'var(--notice-motion-in)' as const,
    noticeMotionOut: 'var(--notice-motion-out)' as const,
    noticeEnterOffset: 'var(--notice-enter-offset)' as const,
    noticeZ: 'var(--notice-z)' as const

} as const;

// Type for all CSS variable keys
export type CSSVarKey = keyof typeof designSystemStyles;

// Helper function to get raw CSS variable name (without var())
export const getCSSVarName = (key: CSSVarKey): string => {
    return designSystemStyles[key].replace('var(', '').replace(')', '');
};

// Export for direct use in styled-components or other CSS-in-JS solutions
export default designSystemStyles;

export const Gap = {
    medium: designSystemStyles.gapMd
}
